import { motion } from "framer-motion";

// Symbol only - circular DNA with light orb
export const LumenSymbol = ({ size = "lg", animated = true }) => {
  const sizes = {
    sm: { dim: 80 },
    md: { dim: 110 },
    lg: { dim: 140 },
    xl: { dim: 180 },
  };

  const { dim } = sizes[size] || sizes.lg;

  return (
    <motion.svg
      width={dim}
      height={dim}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={animated ? { opacity: 0, scale: 0.9 } : {}}
      animate={animated ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <defs>
        {/* Elegant metallic gold gradient */}
        <linearGradient id="goldElegant" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D4AF37" />
          <stop offset="25%" stopColor="#C9A227" />
          <stop offset="50%" stopColor="#E8D48B" />
          <stop offset="75%" stopColor="#C9A227" />
          <stop offset="100%" stopColor="#8B7014" />
        </linearGradient>

        {/* Central light orb - soft glow */}
        <radialGradient id="lightOrb" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFEF8" stopOpacity="1" />
          <stop offset="40%" stopColor="#F5E6B3" stopOpacity="0.9" />
          <stop offset="70%" stopColor="#D4AF37" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#C9A227" stopOpacity="0" />
        </radialGradient>

        {/* Subtle eye gradient - barely visible */}
        <radialGradient id="subtleEye" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0.12" />
          <stop offset="60%" stopColor="#2a2a2a" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#3a3a3a" stopOpacity="0" />
        </radialGradient>

        {/* Soft gold glow */}
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feFlood floodColor="#D4AF37" floodOpacity="0.3" />
          <feComposite in2="blur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g transform="translate(10, 10)">
        {/* DNA Circle - Main ring */}
        <motion.circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="url(#goldElegant)"
          strokeWidth="2.5"
          filter="url(#softGlow)"
          initial={animated ? { pathLength: 0, opacity: 0 } : {}}
          animate={animated ? { pathLength: 1, opacity: 1 } : {}}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />

        {/* DNA Double Helix - outer strand */}
        <motion.path
          d="M50 4 
             C78 14, 96 35, 96 50
             C96 65, 78 86, 50 96
             C22 86, 4 65, 4 50
             C4 35, 22 14, 50 4"
          fill="none"
          stroke="url(#goldElegant)"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.5"
          initial={animated ? { pathLength: 0 } : {}}
          animate={animated ? { pathLength: 1 } : {}}
          transition={{ duration: 1.5, ease: "easeInOut", delay: 0.2 }}
        />

        {/* DNA inner helix strand */}
        <motion.path
          d="M50 15
             C70 23, 82 38, 82 50
             C82 62, 70 77, 50 85
             C30 77, 18 62, 18 50
             C18 38, 30 23, 50 15"
          fill="none"
          stroke="url(#goldElegant)"
          strokeWidth="1.3"
          strokeLinecap="round"
          opacity="0.35"
          initial={animated ? { pathLength: 0 } : {}}
          animate={animated ? { pathLength: 1 } : {}}
          transition={{ duration: 1.3, ease: "easeInOut", delay: 0.3 }}
        />

        {/* DNA horizontal connection bars (base pairs) */}
        {[25, 40, 60, 75].map((y, i) => {
          const xOffset = Math.abs(50 - y) * 0.5;
          return (
            <motion.line
              key={y}
              x1={15 + xOffset}
              y1={y}
              x2={85 - xOffset}
              y2={y}
              stroke="url(#goldElegant)"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.3"
              initial={animated ? { scaleX: 0 } : {}}
              animate={animated ? { scaleX: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.8 + i * 0.1 }}
            />
          );
        })}

        {/* === CENTRAL LIGHT ORB === */}
        
        {/* Outer soft glow */}
        <motion.circle
          cx="50"
          cy="50"
          r="22"
          fill="url(#lightOrb)"
          initial={animated ? { scale: 0, opacity: 0 } : {}}
          animate={animated ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        />

        {/* Inner light core */}
        <motion.circle
          cx="50"
          cy="50"
          r="12"
          fill="#FFFEF8"
          opacity="0.95"
          initial={animated ? { scale: 0 } : {}}
          animate={animated ? { scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
        />

        {/* Subtle eye suggestion - barely visible iris */}
        <motion.circle
          cx="50"
          cy="50"
          r="7"
          fill="url(#subtleEye)"
          initial={animated ? { scale: 0, opacity: 0 } : {}}
          animate={animated ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.4, delay: 1 }}
        />

        {/* Tiny pupil hint - very subtle */}
        <motion.circle
          cx="50"
          cy="50"
          r="2.5"
          fill="#2a2a2a"
          opacity="0.1"
          initial={animated ? { scale: 0 } : {}}
          animate={animated ? { scale: 1 } : {}}
          transition={{ duration: 0.3, delay: 1.1 }}
        />

        {/* Light reflection dot */}
        <motion.circle
          cx="47"
          cy="47"
          r="2"
          fill="#FFFFFF"
          opacity="0.6"
          initial={animated ? { scale: 0 } : {}}
          animate={animated ? { scale: 1 } : {}}
          transition={{ duration: 0.2, delay: 1.2 }}
        />
      </g>
    </motion.svg>
  );
};

// Text only - LUMEN in gold
export const LumenText = ({ animated = true }) => {
  return (
    <motion.div
      className="font-cinzel text-4xl md:text-5xl tracking-[0.3em] font-semibold"
      style={{
        background: "linear-gradient(180deg, #D4AF37 0%, #B8942B 50%, #8B7014 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        textShadow: "1px 2px 3px rgba(90, 74, 10, 0.2)",
      }}
      initial={animated ? { opacity: 0, y: 10 } : {}}
      animate={animated ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      LUMEN
    </motion.div>
  );
};

// Combined logo - Symbol + Text vertically stacked
export const LumenLogo = ({ size = "lg", animated = true }) => {
  return (
    <div className="flex flex-col items-center gap-3">
      <LumenSymbol size={size} animated={animated} />
      <LumenText animated={animated} />
    </div>
  );
};
